import { toastr } from 'react-redux-toastr';
import _ from 'lodash';

const FACEBOOK_APP = {
  dev: {
    id: 864403770758300,
    appSecret: '27f42bb21a9ddeff885012ab9ddfdc15',
    webhookUrl: 'https://orq-boteria.sa.ngrok.io',
    webhookVerifyToken: '515b87d6deb910e010acd8376efd5694',
  },
  homolog: {
    id: 894138031117196,
    appSecret: '967156967fc5cc0e5f67b922ec529421',
    webhookUrl: 'https://wapp.hmlbots.digitalcontact.cloud',
    webhookVerifyToken: '515b87d6deb910e010acd8376efd5694',
  },
  prod: {
    id: 894138031117196,
    appSecret: '967156967fc5cc0e5f67b922ec529421',
    webhookUrl: 'https://wapp.boteria.com.br',
    webhookVerifyToken: '515b87d6deb910e010acd8376efd5694',
  },
};

export const FACEBOOK_SCOPE =
  'pages_show_list,pages_messaging,pages_manage_metadata,pages_read_engagement';

export const CURRENT_FACEBOOK_APP =
  FACEBOOK_APP[process.env.REACT_APP_FACEBOOK.toLowerCase() || 'dev'];

export const FACEBOOK_AUTH_RESET = {
  id: '',
  userID: '',
  accessToken: '',
  name: '',
  expiresIn: 0,
  picture: {
    data: {
      height: 0,
      width: 0,
      url: '',
    },
  },
};

/**
 * Display the toastr for inform user something went wrong and return the object
 * with success value of false
 */
const facebookApiToastrError = () => {
  toastr.error('Erro', 'Ocorreu um erro inesperado ao acessar o Facebook');
  return { facebookSuccess: false };
};

/**
 * Check if Facebook Graph API request was success or not
 * @param {Object} response
 */
const checkFacebookResponse = (response) => {
  return (
    response && !response.hasOwnProperty('facebookSuccess') && !response.error
  );
};

/**
 * Make request on Facebook Graph API for getting the user pages his own
 * @param {object} facebookAuth
 */
export const searchUserPages = async (facebookAuth) => {
  return new Promise(async (resolve) => {
    const facebook = window.FB;
    const { id, longLivedAccessToken } = facebookAuth;
    const { access_token } = longLivedAccessToken;
    const url = `/${id}/accounts?access_token=${access_token}`;

    facebook.api(url, 'GET', async (response) => {
      if (response && !response.error) {
        const pagesWithPicture = await getPagesPictures(response.data);
        resolve(pagesWithPicture);
      } else {
        resolve(facebookApiToastrError());
      }
    });
  });
};

/**
 * Make the Facebook App and Webhook link for selected user page
 * @param {Object} page
 */
export const linkFacebookPageToApp = async (page) => {
  return new Promise(async (resolve) => {
    const subscribeApp = await subscribeFacebookPageToApp(page);
    if (!checkFacebookResponse(subscribeApp)) {
      resolve(facebookApiToastrError());
    }

    const subscribeWebhook = await subscribeFacebookPageWebhook(page);
    if (checkFacebookResponse(subscribeWebhook)) {
      resolve({ subscribeWebhook, subscribeApp });
    } else {
      resolve(facebookApiToastrError());
    }
  });
};

/**
 * Logout the current Facebook user and execute a callback function after this
 * @param {Function} callBack
 */
export const userLogout = (callBack) => {
  const facebook = window.FB;
  facebook.logout(() => callBack());
};

/**
 * Get long lived access token (60 days) fro user
 * @param {string} accessToken token from user that is logged
 */
export const getUserLongLivedAccessToken = (accessToken) => {
  return new Promise(async (resolve) => {
    const facebook = window.FB;
    const url = `/oauth/access_token?grant_type=fb_exchange_token&client_id=${CURRENT_FACEBOOK_APP.id}&client_secret=${CURRENT_FACEBOOK_APP.appSecret}&fb_exchange_token=${accessToken}`;

    facebook.api(url, 'GET', (response) => {
      if (response && !response.error) {
        resolve(response);
      } else {
        resolve(facebookApiToastrError());
      }
    });
  });
};

/**
 * Get a long lived access token (forever or until user remove app) user pages
 * @param {object} facebookAuth reference for auth state
 */
export const getPageLongLivedAccessToken = (facebookAuth) => {
  return new Promise(async (resolve) => {
    const facebook = window.FB;
    const { id, longLivedAccessToken } = facebookAuth;
    const { access_token } = longLivedAccessToken;
    const url = `/${id}/accounts?access_token=${access_token}`;

    facebook.api(url, 'GET', (response) => {
      if (response && !response.error) {
        resolve(response);
      } else {
        resolve(facebookApiToastrError());
      }
    });
  });
};

/**
 * Subscribe the user Facebook page on ours app Webhook
 * @param {object} page
 */
const subscribeFacebookPageWebhook = async (page) => {
  return new Promise(async (resolve) => {
    const facebook = window.FB;
    const appClientAccessToken = `${CURRENT_FACEBOOK_APP.id}|${CURRENT_FACEBOOK_APP.appSecret}`;
    const url = `/${CURRENT_FACEBOOK_APP.id}/subscriptions`;
    const body = {
      object: 'page',
      callback_url: `${CURRENT_FACEBOOK_APP.webhookUrl}/facebook-messenger/webhook`,
      fields:
        'messages, messaging_postbacks, messaging_optins, message_deliveries, message_reads, message_reactions',
      include_values: true,
      verify_token: CURRENT_FACEBOOK_APP.webhookVerifyToken,
      access_token: appClientAccessToken,
    };

    facebook.api(url, 'POST', body, (response) => {
      if (response && !response.error) {
        resolve(response);
      } else {
        resolve({ facebookSuccess: false });
      }
    });
  });
};

/**
 * Subscribe user Facebook Page to the our Facebook App
 * @param {Object} page
 */
const subscribeFacebookPageToApp = async (page) => {
  return new Promise(async (resolve) => {
    const facebook = window.FB;
    const url = `/${page.id}/subscribed_apps?access_token=${page.access_token}`;
    const fields =
      'messages, messaging_postbacks, messaging_optins, message_deliveries, messaging_referrals';
    const body = {
      subscribed_fields: fields,
    };

    facebook.api(url, 'POST', body, (response) => {
      if (response && !response.error) {
        resolve(response);
      } else {
        resolve({ facebookSuccess: false });
      }
    });
  });
};

/**
 * Get picture for the Facebook page passed
 * @param {Object} page
 */
const getPagePicture = async (page) => {
  return new Promise(async (resolve) => {
    const facebook = window.FB;
    facebook.api(`/${page.id}/picture`, { redirect: 0 }, (response) => {
      if (response && !response.error) {
        resolve({ ...page, picture: { ...response } });
      } else {
        resolve({ ...page });
      }
    });
  });
};

/**
 * Get pictures for each Facebook page from current user
 * @param {Object[]} pages
 */
const getPagesPictures = async (pages) => {
  return new Promise(async (resolve) => {
    const newPages = await Promise.all(
      _.map(pages, async (page) => {
        const newPageValue = await getPagePicture(page);
        return newPageValue;
      })
    );

    resolve(newPages);
  });
};
